import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileCatOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  const goHomeAndScroll = () => {
    if (isHome) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-background-50/98 backdrop-blur-md border-b border-background-300/60"
      style={{ height: '64px' }}
    >
      <div className="flex items-center justify-between h-full px-4 md:px-6 max-w-[1440px] mx-auto">
        {/* Logo / Brand */}
        <Link
          to="/"
          className="flex items-center gap-2 group flex-shrink-0"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-file-list-3-line text-base"></i>
          </div>
          <span className="text-sm md:text-base font-semibold text-foreground-900 whitespace-nowrap font-heading">
            USFansSpreadsheet
          </span>
        </Link>

        {/* Desktop Right Side */}
        <div className="hidden md:flex items-center gap-2 lg:gap-3">
          {/* Home button */}
          <button
            onClick={goHomeAndScroll}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer bg-transparent"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-home-4-line text-sm"></i>
            </div>
            <span className="hidden lg:inline">Strona główna</span>
          </button>

          {/* Shopping Guide button */}
          <button
            onClick={() => {
              const el = document.getElementById('kategorie-produktow');
              if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
              } else {
                navigate('/');
                setTimeout(() => {
                  const el2 = document.getElementById('kategorie-produktow');
                  if (el2) el2.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }
              setIsDropdownOpen(false);
            }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer bg-transparent"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shopping-bag-3-line text-sm"></i>
            </div>
            <span className="hidden lg:inline">Przewodnik zakupów</span>
          </button>

          {/* Category Dropdown */}
          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer bg-transparent"
            >
              <span>Kategorie</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={isDropdownOpen ? 'ri-arrow-up-s-line text-xs' : 'ri-arrow-down-s-line text-xs'}></i>
              </div>
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full right-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200 shadow-lg py-2 max-h-[420px] overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-100">
                      <i className={`${cat.icon} text-sm text-foreground-600`}></i>
                    </div>
                    <span>{cat.namePl}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* CTA Buttons */}
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="px-4 py-2 text-sm font-medium text-foreground-800 border border-foreground-300/50 rounded-full hover:border-foreground-500 hover:text-foreground-900 transition-all whitespace-nowrap cursor-pointer"
          >
            usfans spreadsheet
          </a>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2 text-sm font-semibold text-background-50 bg-foreground-900 rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
          >
            buy it now
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer bg-transparent"
        >
          <i className={isMobileMenuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 shadow-lg">
          <div className="px-4 py-3 max-h-[70vh] overflow-y-auto">
            {/* Quick nav buttons */}
            <div className="flex gap-2 mb-3">
              <button
                onClick={goHomeAndScroll}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold text-foreground-700 bg-background-100 rounded-xl hover:bg-background-200 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-4-line text-sm"></i>
                </div>
                Strona główna
              </button>
              <button
                onClick={() => {
                  navigate('/');
                  setTimeout(() => {
                    const el = document.getElementById('kategorie-produktow');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }, 200);
                  setIsMobileMenuOpen(false);
                }}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold text-foreground-700 bg-background-100 rounded-xl hover:bg-background-200 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shopping-bag-3-line text-sm"></i>
                </div>
                Przewodnik
              </button>
            </div>

            {/* Mobile Categories Toggle */}
            <button
              onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
              className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-foreground-900 bg-background-100 rounded-xl cursor-pointer"
            >
              <span>Kategorie produktów</span>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={isMobileCatOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
              </div>
            </button>

            {isMobileCatOpen && (
              <div className="grid grid-cols-2 gap-1.5 mt-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2.5 text-xs text-foreground-700 bg-background-50 border border-background-200 rounded-xl hover:border-accent-300 hover:bg-background-100 transition-all cursor-pointer"
                  >
                    <div className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-100 flex-shrink-0">
                      <i className={`${cat.icon} text-xs text-accent-600`}></i>
                    </div>
                    <span className="truncate">{cat.namePl}</span>
                  </a>
                ))}
              </div>
            )}

            {/* CTA buttons */}
            <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-background-200">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-3 text-sm font-semibold text-foreground-800 border border-foreground-300/50 rounded-full text-center hover:border-foreground-500 transition-all whitespace-nowrap cursor-pointer"
              >
                usfans spreadsheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-3 text-sm font-semibold text-background-50 bg-foreground-900 rounded-full text-center hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
              >
                buy it now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}